import { useEffect, useState } from "react";
import {
  Instagram,
  LogOut,
  MessagesSquare,
  Settings2,
  UserRound,
} from "lucide-react";
import { Botao, MolduraDoApp, cn } from "@base/ui";
import { PageLayout } from "@/components/layout/page-header";
import { useAuth } from "./auth.contexto";
import { LogoRenkeRefs } from "../branding/logo-renke-refs";
import { usePropsDoSeletorNaMoldura } from "../tenant/projeto.contexto";
import { useNavegacaoDoApp } from "../../modules/referencias/presentation/navegacao-do-app";
import { rodapeDaSessaoNaLateral } from "../../modules/referencias/presentation/rodape-da-sessao";
import {
  ConteudoIntegracoesDaConta,
  type AbaIntegracaoDaConta,
} from "../../modules/referencias/presentation/conteudo-configuracoes-da-conta";
import { PainelMonitoramentoDeGrupos } from "../../modules/monitoramento-grupos/presentation/painel-monitoramento-grupos";

type AbaDaConta = "conta" | "monitoramento-grupos" | AbaIntegracaoDaConta;

function lerAbaDoHash(): AbaDaConta {
  const query = window.location.hash.split("?")[1] ?? "";
  const aba = new URLSearchParams(query).get("aba");
  if (
    aba === "instagram" ||
    aba === "clickup" ||
    aba === "conta" ||
    aba === "monitoramento-grupos"
  ) {
    return aba;
  }
  return "conta";
}

function escreverAbaNoHash(aba: AbaDaConta): void {
  window.location.hash = aba === "conta" ? "perfil" : `perfil?aba=${aba}`;
}

/**
 * Configurações da conta: sessão, integrações do estúdio e monitoramento de grupos.
 * Global ao usuário (não ao projeto).
 */
export function PaginaDePerfil() {
  const { sessao, sair } = useAuth();
  const seletorProjeto = usePropsDoSeletorNaMoldura();
  const navegacao = useNavegacaoDoApp("perfil");
  const [aba, definirAba] = useState<AbaDaConta>(() => lerAbaDoHash());

  useEffect(() => {
    const sync = () => definirAba(lerAbaDoHash());
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  function selecionarAba(id: AbaDaConta) {
    definirAba(id);
    escreverAbaNoHash(id);
  }

  const nome =
    sessao?.nome?.trim() || sessao?.cliente?.trim() || "Sem nome";
  const email = sessao?.email?.trim() || null;
  const inicial = nome.slice(0, 1).toUpperCase();

  const itensNav: ReadonlyArray<{
    readonly id: AbaDaConta;
    readonly rotulo: string;
    readonly descricao: string;
    readonly Icone: typeof UserRound;
  }> = [
    {
      id: "conta",
      rotulo: "Dados da conta",
      descricao: "Nome, e-mail e sessão",
      Icone: UserRound,
    },
    {
      id: "instagram",
      rotulo: "Instagram (estúdio)",
      descricao: "Coleções e stories",
      Icone: Instagram,
    },
    {
      id: "clickup",
      rotulo: "ClickUp",
      descricao: "Token e lista",
      Icone: Settings2,
    },
    {
      id: "monitoramento-grupos",
      rotulo: "Monitoramento de Grupos",
      descricao: "Acompanhar conversas e alertas",
      Icone: MessagesSquare,
    },
  ];

  return (
    <MolduraDoApp
      nomeDoApp="Creative OS"
      marca={<LogoRenkeRefs />}
      titulo="Configurações da conta"
      subtitulo="Sua sessão e integrações do estúdio (não do projeto)"
      navegacao={navegacao}
      rodapeDaLateral={rodapeDaSessaoNaLateral}
      {...seletorProjeto}
    >
      <PageLayout className="animate-aparecer gap-8 pb-24">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 lg:flex-row lg:items-start lg:gap-10">
          <nav
            aria-label="Configurações da conta"
            className="flex shrink-0 gap-1 overflow-x-auto pb-1 lg:sticky lg:top-4 lg:w-56 lg:flex-col lg:overflow-visible lg:pb-0"
          >
            {itensNav.map(({ id, rotulo, descricao, Icone }) => {
              const ativo = aba === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => selecionarAba(id)}
                  aria-current={ativo ? "page" : undefined}
                  className={cn(
                    "flex min-w-[10.5rem] cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:min-w-0 lg:w-full",
                    ativo
                      ? "border-border bg-accent text-accent-foreground"
                      : "border-transparent text-muted-foreground hover:border-border/60 hover:bg-muted/60 hover:text-foreground",
                  )}
                >
                  <Icone className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold tracking-tight">
                      {rotulo}
                    </span>
                    <span className="mt-0.5 hidden text-xs leading-snug text-muted-foreground lg:block">
                      {descricao}
                    </span>
                  </span>
                </button>
              );
            })}
          </nav>

          <div className="min-w-0 flex-1">
            {aba === "conta" ? (
              <div className="mx-auto max-w-xl space-y-8">
                <div className="flex items-center gap-4">
                  <span
                    className="grid size-14 shrink-0 place-items-center rounded-2xl bg-primary text-lg font-semibold tracking-tight text-primary-foreground"
                    aria-hidden="true"
                  >
                    {inicial}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-xl font-semibold tracking-tight text-foreground">
                      {nome}
                    </p>
                    <p className="truncate text-sm text-muted-foreground">
                      {email ?? "E-mail não informado nesta sessão"}
                    </p>
                  </div>
                </div>

                <dl className="divide-y divide-border border-y border-border">
                  <Linha rotulo="Nome" valor={nome} />
                  <Linha rotulo="E-mail" valor={email ?? "Não disponível"} />
                </dl>

                <section className="space-y-3 rounded-xl border border-border/70 bg-card p-4">
                  <h2 className="text-sm font-semibold tracking-tight text-foreground">
                    Sessão
                  </h2>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    A sessão vale neste navegador, em qualquer projeto. Ao sair,
                    entre de novo com e-mail e senha.
                  </p>
                  <Botao
                    variante="contorno"
                    onClick={() => void sair()}
                    aria-label="Sair"
                  >
                    <LogOut className="size-4" aria-hidden="true" />
                    Sair
                  </Botao>
                </section>

                <p className="text-sm text-muted-foreground">
                  Instagram do estúdio, ClickUp e Monitoramento de Grupos ficam
                  nas abas ao lado. Para publicar no Instagram do cliente, use
                  Configurações do projeto → Redes do cliente.
                </p>
              </div>
            ) : null}

            {aba === "monitoramento-grupos" ? (
              <PainelMonitoramentoDeGrupos />
            ) : null}

            {aba === "instagram" || aba === "clickup" ? (
              <ConteudoIntegracoesDaConta
                abaControlada={aba}
                onMudarAba={(proxima) => selecionarAba(proxima)}
                esconderNavegacao
              />
            ) : null}
          </div>
        </div>
      </PageLayout>
    </MolduraDoApp>
  );
}

function Linha({
  rotulo,
  valor,
}: {
  readonly rotulo: string;
  readonly valor: string;
}) {
  return (
    <div className="flex flex-col gap-1 py-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
      <dt className="text-sm text-muted-foreground">{rotulo}</dt>
      <dd className="truncate text-sm font-medium tracking-tight text-foreground sm:text-right">
        {valor}
      </dd>
    </div>
  );
}
